import { getBackendUrl } from '../config/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus,
  Check,
  Calendar,
  Trash2,
  Edit2,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  Users,
  User as UserIcon,
  Star
} from 'lucide-react';

const TasksPage = () => {
  const API = getBackendUrl() || '';
  
  // State
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'today', 'week', 'overdue'
  const [expandedDates, setExpandedDates] = useState(new Set());
  
  // Form state
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    task_type: 'personal',
    priority: 'medium'
  });

  // Fetch tasks
  const fetchTasks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data);
      
      // Auto-expand today's section
      const today = new Date().toISOString().split('T')[0];
      setExpandedDates(new Set([today]));
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  // Create task
  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/tasks`, taskForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Task created successfully');
      setShowAddModal(false);
      setTaskForm({
        title: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        task_type: 'personal',
        priority: 'medium'
      });
      fetchTasks();
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Failed to create task');
    }
  };

  // Update task
  const handleUpdateTask = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/api/tasks/${editingTask.id}`, taskForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Task updated successfully');
      setShowEditModal(false);
      setEditingTask(null);
      fetchTasks();
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };

  // Delete task
  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    console.log('[TASKS] Deleting task:', taskId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/api/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[TASKS] Delete response:', response.data);
      toast.success('Task deleted successfully');
      fetchTasks();
    } catch (error) {
      console.error('[TASKS] Error deleting task:', error);
      console.error('[TASKS] Error response:', error.response?.data);
      const errorMsg = error.response?.data?.detail || 'Failed to delete task';
      toast.error(errorMsg);
    }
  };

  // Toggle task status
  const handleToggleStatus = async (task) => {
    try {
      const token = localStorage.getItem('token');
      const newStatus = task.status === 'completed' ? 'pending' : 'completed';
      await axios.patch(`${API}/api/tasks/${task.id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Task marked as ${newStatus}`);
      fetchTasks();
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error('Failed to update task status');
    }
  };

  // Open edit modal
  const openEditModal = (task) => {
    setEditingTask(task);
    setTaskForm({
      title: task.title,
      description: task.description || '',
      date: task.date,
      task_type: task.task_type,
      priority: task.priority || 'medium'
    });
    setShowEditModal(true);
  };

  // Filter tasks
  const getFilteredTasks = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString().split('T')[0];
    
    const weekEnd = new Date(today);
    weekEnd.setDate(weekEnd.getDate() + 7);
    const weekEndStr = weekEnd.toISOString().split('T')[0];

    switch (filter) {
      case 'today':
        return tasks.filter(t => t.date === todayStr);
      case 'week':
        return tasks.filter(t => t.date >= todayStr && t.date <= weekEndStr);
      case 'overdue':
        return tasks.filter(t => t.date < todayStr && t.status !== 'completed');
      default:
        return tasks;
    }
  };

  // Group tasks by date
  const groupTasksByDate = () => {
    const filtered = getFilteredTasks();
    const grouped = {};
    
    filtered.forEach(task => {
      if (!grouped[task.date]) {
        grouped[task.date] = [];
      }
      grouped[task.date].push(task);
    });
    
    // Sort dates
    return Object.keys(grouped).sort().map(date => ({
      date,
      tasks: grouped[date]
    }));
  };

  // Toggle date expansion
  const toggleDateExpansion = (date) => {
    const newExpanded = new Set(expandedDates);
    if (newExpanded.has(date)) {
      newExpanded.delete(date);
    } else {
      newExpanded.add(date);
    }
    setExpandedDates(newExpanded);
  };

  // Format date display
  const formatDateDisplay = (dateStr) => {
    // Get today's date in YYYY-MM-DD format (local timezone)
    const now = new Date();
    const todayStr = now.toLocaleDateString('en-CA'); // en-CA gives YYYY-MM-DD format
    
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toLocaleDateString('en-CA');
    
    if (dateStr === todayStr) {
      return 'Today';
    } else if (dateStr === tomorrowStr) {
      return 'Tomorrow';
    } else {
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  // Check if date is overdue
  const isOverdue = (dateStr) => {
    const todayStr = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format
    return dateStr < todayStr;
  };

  // Priority badge colors
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const groupedTasks = groupTasksByDate();

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-3xl font-bold text-gray-900">Task Management</h1>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Task
          </button>
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          {['all', 'today', 'week', 'overdue'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg capitalize transition-colors ${
                filter === f
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {f === 'week' ? 'This Week' : f}
            </button>
          ))}
        </div>
      </div>

      {/* Tasks List - Agenda View */}
      <div className="space-y-2">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading tasks...</div>
        ) : groupedTasks.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <p>No tasks found</p>
          </div>
        ) : (
          groupedTasks.map(({ date, tasks: dateTasks }) => (
            <div key={date} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Date Header */}
              <button
                onClick={() => toggleDateExpansion(date)}
                className={`w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors ${
                  isOverdue(date) && dateTasks.some(t => t.status !== 'completed')
                    ? 'bg-red-50'
                    : 'bg-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  {expandedDates.has(date) ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-900">
                      {formatDateDisplay(date)}
                    </h3>
                    <p className="text-sm text-gray-500">{date}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-500">
                    {dateTasks.filter(t => t.status === 'completed').length}/{dateTasks.length} completed
                  </span>
                  {isOverdue(date) && dateTasks.some(t => t.status !== 'completed') && (
                    <span className="text-xs bg-red-500 text-white px-2 py-1 rounded">
                      Overdue
                    </span>
                  )}
                </div>
              </button>

              {/* Tasks for this date */}
              {expandedDates.has(date) && (
                <div className="bg-gray-50 divide-y divide-gray-200">
                  {dateTasks.map(task => (
                    <div
                      key={task.id}
                      className="p-4 bg-white hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        {/* Status Checkbox */}
                        <button
                          onClick={() => handleToggleStatus(task)}
                          className="mt-1"
                        >
                          {task.status === 'completed' ? (
                            <div className="w-5 h-5 bg-green-500 rounded flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          ) : (
                            <div className="w-5 h-5 border-2 border-gray-300 rounded hover:border-blue-500" />
                          )}
                        </button>

                        {/* Task Content */}
                        <div className="flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className={`font-medium ${
                                  task.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-900'
                                }`}>
                                  {task.title}
                                </h4>
                                
                                {/* Task Type Badge - More Prominent */}
                                {task.task_type === 'shared' ? (
                                  <span className="inline-flex items-center gap-1 text-xs bg-purple-500 text-white px-2 py-0.5 rounded font-medium">
                                    <Users className="w-3 h-3" />
                                    SHARED
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-xs bg-blue-500 text-white px-2 py-0.5 rounded font-medium">
                                    <UserIcon className="w-3 h-3" />
                                    PERSONAL
                                  </span>
                                )}
                              </div>
                              
                              {/* Description */}
                              {task.description && (
                                <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                              )}
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => openEditModal(task)}
                                className="flex items-center gap-1 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-2 py-1 rounded transition-colors"
                                title="Edit or reschedule task"
                              >
                                <Calendar className="w-3 h-3" />
                                Reschedule
                              </button>
                              <button
                                onClick={() => openEditModal(task)}
                                className="text-blue-600 hover:text-blue-800 p-1"
                                title="Edit task"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteTask(task.id)}
                                className="text-red-600 hover:text-red-800 p-1"
                                title="Delete task"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          {/* Meta Information */}
                          <div className="flex items-center gap-3 mt-2">
                            {/* Priority */}
                            <span className={`text-xs px-2 py-1 rounded capitalize font-medium ${getPriorityColor(task.priority)}`}>
                              ⚡ {task.priority} priority
                            </span>

                            {/* Created by */}
                            <span className="text-xs text-gray-500">
                              Created by: {task.user_email}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add Task Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4">Add New Task</h2>
            <form onSubmit={handleCreateTask} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows="3"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date *
                </label>
                <input
                  type="date"
                  value={taskForm.date}
                  onChange={(e) => setTaskForm({ ...taskForm, date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Task Type *
                </label>
                <select
                  value={taskForm.task_type}
                  onChange={(e) => setTaskForm({ ...taskForm, task_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="personal">Personal</option>
                  <option value="shared">Shared (visible to all admins)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority *
                </label>
                <select
                  value={taskForm.priority}
                  onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Create Task
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setTaskForm({
                      title: '',
                      description: '',
                      date: new Date().toISOString().split('T')[0],
                      task_type: 'personal',
                      priority: 'medium'
                    });
                  }}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4">Edit Task</h2>
            <form onSubmit={handleUpdateTask} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows="3"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date *
                </label>
                <input
                  type="date"
                  value={taskForm.date}
                  onChange={(e) => setTaskForm({ ...taskForm, date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Task Type *
                </label>
                <select
                  value={taskForm.task_type}
                  onChange={(e) => setTaskForm({ ...taskForm, task_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="personal">Personal</option>
                  <option value="shared">Shared (visible to all admins)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority *
                </label>
                <select
                  value={taskForm.priority}
                  onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Update Task
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingTask(null);
                  }}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksPage;
